import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  SiReact, SiAngular, SiTypescript, SiJavascript, 
  SiHtml5, SiCss3, SiSass, SiBootstrap, SiTailwindcss, SiWebpack, SiRedux, 
  SiNodedotjs, SiPython, SiDjango, SiRubyonrails,  SiSpringboot, 
  SiPhp, SiLaravel, SiExpress, SiMysql, SiPostgresql, SiMongodb, SiRedis, 
  SiDocker, SiKubernetes 
} from "react-icons/si";

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(52, 152, 219, 0.5); }
  50% { box-shadow: 0 0 20px rgba(52, 152, 219, 0.8); }
  100% { box-shadow: 0 0 5px rgba(52, 152, 219, 0.5); }
`;

// Styled components
const Container = styled.div`
  padding: 1rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  
  @media (min-width: 768px) {
    padding: 1rem 2rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 0.8rem;
  color: #2c3e50;
  font-weight: 600;
  background: linear-gradient(135deg, #3498db, #2c3e50);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${fadeIn} 1s ease-out;
  
  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    padding: 0;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #7f8c8d;
  margin-bottom: 2rem;
  animation: ${fadeIn} 1s ease-out 0.2s forwards;
  opacity: 0;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 3rem;
    padding: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: #3498db;
  margin: 2rem 0 1.2rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #eaeaea;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 50px;
    height: 2px;
    background: #3498db;
  }
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
    margin: 2.5rem 0 1.5rem;
    
    &::after {
      width: 60px;
    }
  }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const TechCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.5s ease-out;
  opacity: 0;
  animation-fill-mode: forwards;
  animation-delay: ${props => props.delay || '0s'};
  display:flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
    animation: ${glow} 2s infinite;
    
    .tech-icon {
      animation: ${float} 1.5s ease-in-out infinite;
      color: #3498db;
    }
  }
  
  @media (min-width: 768px) {
    border-radius: 10px;
    padding: 1.2rem;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  font-size: 2.2rem;
  margin-bottom: 0.6rem;
  color: #12066dff;
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    font-size: 3rem;
    margin-bottom: 0.8rem;
  }
`;

const TechName = styled.p`
  margin: 0;
  font-weight: 600;
  color: #002e5dff;
  font-size: 0.8rem;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Note = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 1.2rem;
  background: linear-gradient(135deg, #f6f9fc, #eaeaea);
  border-radius: 8px;
  font-style: italic;
  color: #7f8c8d;
  border-left: 4px solid #3498db;
  font-size: 0.9rem;
  
  @media (min-width: 768px) {
    padding: 1.5rem;
    border-radius: 10px;
    font-size: 1rem;
  }
`;

const TechnologyStack = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

const frontendTech = [
  { name: 'React', icon: <SiReact /> },
 
  { name: 'Angular', icon: <SiAngular /> },
  { name: 'TypeScript', icon: <SiTypescript /> },
  { name: 'JavaScript', icon: <SiJavascript /> },
  { name: 'HTML5', icon: <SiHtml5 /> },
  { name: 'CSS3', icon: <SiCss3 /> },
  { name: 'SASS', icon: <SiSass /> },
  { name: 'Bootstrap', icon: <SiBootstrap /> },
  { name: 'Tailwind CSS', icon: <SiTailwindcss /> },
  { name: 'Webpack', icon: <SiWebpack /> },
  { name: 'Redux', icon: <SiRedux /> },
];

const backendTech = [
  { name: 'Node.js', icon: <SiNodedotjs /> },
  { name: 'Python', icon: <SiPython /> },
  { name: 'Django', icon: <SiDjango /> },
  { name: 'Ruby on Rails', icon: <SiRubyonrails /> },

  { name: 'Spring Boot', icon: <SiSpringboot /> },
  { name: 'PHP', icon: <SiPhp /> },
  { name: 'Laravel', icon: <SiLaravel /> },
  { name: 'Express.js', icon: <SiExpress /> },
  { name: 'MySQL', icon: <SiMysql /> },
  { name: 'PostgreSQL', icon: <SiPostgresql /> },
  { name: 'MongoDB', icon: <SiMongodb /> },
  { name: 'Redis', icon: <SiRedis /> },
  { name: 'Docker', icon: <SiDocker /> },
  { name: 'Kubernetes', icon: <SiKubernetes /> },
 
];

  return (
    <Container>
      <Title>Our Technology Stack</Title>
      <Subtitle>Cutting-edge tools and technologies we use to build amazing products</Subtitle>
      
      <SectionTitle>Frontend Technologies</SectionTitle>
      <TechGrid>
        {frontendTech.map((tech, index) => (
          <TechCard 
            key={tech.name} 
            delay={`${0.1 + index * 0.05}s`}
            style={{ opacity: isVisible ? 1 : 0 }}
          >
            <TechIcon>{tech.icon}</TechIcon>
            <TechName>{tech.name}</TechName>
          </TechCard>
        ))}
      </TechGrid>
      
      <SectionTitle>Backend Technologies</SectionTitle>
      <TechGrid>
        {backendTech.map((tech, index) => (
          <TechCard 
            key={tech.name} 
            delay={`${0.1 + index * 0.05}s`}
            style={{ opacity: isVisible ? 1 : 0 }}
          >
            <TechIcon>{tech.icon}</TechIcon>
            <TechName>{tech.name}</TechName>
          </TechCard>
        ))}
      </TechGrid>
      
      <Note>
        <p>We continuously evaluate and adopt new technologies to ensure our solutions are built with the most effective and modern tools available.</p>
      </Note>
    </Container>
  );
};

export default TechnologyStack;